import { createModel } from "@rematch/core";
import type { AxiosError } from "axios";
import type global from "global";
import { toast } from "react-toastify";
import request from "request";

export type ZustandState<T> = {
	pending: boolean;
	success: boolean;
	error: boolean;
	data: T | null;
	errorData: string | null;
};

const initialValue: ZustandState<null> = {
	pending: false,
	error: false,
	data: null,
	errorData: null,
	success: false,
};

const permissions = createModel()({
	state: initialValue as global.contextApplication,

	reducers: {
		START_PENDING(state) {
			return { ...state, pending: true, success: false, error: false };
		},
		STOP_PENDING(state) {
			return { ...state, pending: false };
		},
		SET_SUCCESS(state, payload: any) {
			return {
				...state,
				pending: false,
				success: true,
				error: false,
				data: payload,
			};
		},
		SET_ERROR(state, payload: any) {
			return {
				...state,
				pending: false,
				success: false,
				error: true,
				errorData: payload,
			};
		},
		RESET() {
			return initialValue;
		},
	},

	effects: (dispatch) => ({
		async execute(args: { username: string }) {
			dispatch.permissions.START_PENDING();
			try {
				const response = await request.get(
					`admin/permissions/${args.username}`,
				);
				const data = response.data.permission_list;
				dispatch.permissions.SET_SUCCESS(data);
			} catch (err: unknown) {
				const error = err as AxiosError;
				const e = toast.error(
					(error?.response?.data as { detail: string })?.detail ??
						"Ошибка при обработке запроса(500)",
				);
				dispatch.permissions.SET_ERROR(e);
			}
		},

		async editPermission({
			username,
			permission,
		}: {
			username: string;
			permission: string;
		}) {
			dispatch.permissions.START_PENDING();
			const id = toast.loading("Загрузка...");
			try {
				const response = await request.put(`admin/permissions/${username}`, {
					permission,
				});
				const data = response.data.permissionList;
				toast.success("Успешно");
				dispatch.permissions.SET_SUCCESS(data);

				await dispatch.permissions.execute({ username });
			} catch (err: unknown) {
				const error = err as AxiosError;
				const e = toast.error(
					(error?.response?.data as { detail: string })?.detail ??
						"Ошибка при обработке запроса(500)",
				);
				dispatch.permissions.SET_ERROR(e);
			}
			toast.dismiss(id);
		},

		async deletePermission({
			username,
			permission,
		}: {
			username: string;
			permission: string;
		}) {
			dispatch.permissions.START_PENDING();
			const id = toast.loading("Загрузка...");
			try {
				const response = await request.delete(`admin/permissions/${username}`, {
					data: { permission },
				});
				const data = response.data.permissionList;
				toast.success("Успешно");
				dispatch.permissions.SET_SUCCESS(data);

				await dispatch.permissions.execute({ username });
			} catch (err: unknown) {
				const error = err as AxiosError;
				const e = toast.error(
					(error?.response?.data as { detail: string })?.detail ??
						"Ошибка при обработке запроса(500)",
				);
				dispatch.permissions.SET_ERROR(e);
			}
			toast.dismiss(id);
		},
		async rolePermission({ role }: { role: string }) {
			dispatch.permissions.START_PENDING();
			try {
				const response = await request.get(`admin/${role}/permissions`);
				const data = response.data.permission_list;

				dispatch.permissions.SET_SUCCESS(data);
			} catch (err: unknown) {
				const error = err as AxiosError;
				const e = toast.error(
					(error?.response?.data as { detail: string })?.detail,
				);
				dispatch.permissions.SET_ERROR(e);
			}
		},

		async getAllPermissions() {
			dispatch.permissions.START_PENDING();
			try {
				const response = await request.get(`admin/permissions`);
				const data =
					response.data.permissions ||
					response.data.permission_list ||
					response.data;

				dispatch.permissions.SET_SUCCESS(data);
			} catch (err: unknown) {
				const error = err as AxiosError;
				console.error("Error fetching all permissions:", error);
				const e = toast.error(
					(error?.response?.data as { detail: string })?.detail ||
						"Error fetching permissions",
				);
				dispatch.permissions.SET_ERROR(e);
			}
		},
	}),
});

export default permissions;
